import { validateSmithersBulkDeviceIds } from "@/lib/smithers-bulk"
import { toSmithersProVenueRequest } from "@/lib/smithers-provenue"
import type { SmithersProVenueSelection } from "@/types/provenue-instance"
import type { BulkUpdateSmithersDevicesRequest, BulkUpdateSmithersDevicesResult, SmithersBulkUpdatePatch } from "@/types/smithers-device"

export const SMITHERS_BULK_PROPERTIES = [
  { key: "validationVendor", label: "Validation Vendor", type: "vendor" },
  { key: "facilityCode", label: "Facility Code", type: "text" },
  { key: "venueId", label: "Venue ID", type: "text" },
  { key: "areaCode", label: "Area Code", type: "text" },
  { key: "deviceCode", label: "Device Code", type: "text" },
  { key: "nteClubCode", label: "NTE Club Code", type: "text" },
  { key: "matchThreshold", label: "Match Threshold", type: "number" },
  { key: "validationTimeoutMs", label: "Validation Timeout (ms)", type: "number" },
  { key: "proVenueInstanceId", label: "ProVenue Instance", type: "provenue" },
] as const
export type SmithersBulkProperty = typeof SMITHERS_BULK_PROPERTIES[number]["key"]

export function toSmithersBulkUpdatePatch(property: SmithersBulkProperty, value: string, useDefault: boolean, selection: SmithersProVenueSelection): SmithersBulkUpdatePatch {
  if (property === "proVenueInstanceId") return toSmithersProVenueRequest(selection)
  if (useDefault) return { [property]: null }
  if (property === "matchThreshold" || property === "validationTimeoutMs") {
    if (!value.trim()) throw new Error("Enter a nonnegative integer")
    return { [property]: Number(value) }
  }
  return { [property]: value } as SmithersBulkUpdatePatch
}

export function validateSmithersBulkUpdateRequest(request: BulkUpdateSmithersDevicesRequest): void {
  validateSmithersBulkDeviceIds(request.ids)
  validateSmithersBulkUpdatePatch(request.update)
}

export function validateSmithersBulkUpdatePatch(patch: SmithersBulkUpdatePatch): void {
  if ("requiresProVenueInstance" in patch) {
    if (Object.keys(patch).some((field) => field !== "requiresProVenueInstance" && field !== "proVenueInstanceId")) throw new Error("A ProVenue update may only contain requiresProVenueInstance and proVenueInstanceId")
    if (typeof patch.requiresProVenueInstance !== "boolean") throw new Error("requiresProVenueInstance must be true or false")
    const id = patch.proVenueInstanceId
    if (id !== undefined && id !== null && (typeof id !== "number" || !Number.isSafeInteger(id) || id < 1)) throw new Error("Select a valid ProVenue instance")
    return
  }
  const fields = Object.entries(patch)
  if (fields.length !== 1) throw new Error("Update must contain exactly one field")
  const [field, value] = fields[0]
  if (!SMITHERS_BULK_PROPERTIES.some((property) => property.key === field)) throw new Error("Unsupported Smithers configuration field")
  if (field === "proVenueInstanceId") throw new Error("proVenueInstanceId must be sent with requiresProVenueInstance")
  if (value === null) return
  if (field === "matchThreshold" || field === "validationTimeoutMs") {
    const max = field === "matchThreshold" ? 2147483647 : Number.MAX_SAFE_INTEGER
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0 || value > max) throw new Error(`Enter an integer between 0 and ${max}`)
  } else if (field === "validationVendor") {
    if (value !== "Orion" && value !== "ProVenue") throw new Error("Select a validation vendor")
  } else if (typeof value !== "string") throw new Error("Enter a text value")
}

export function isSmithersBulkUpdateResult(value: unknown): value is BulkUpdateSmithersDevicesResult {
  if (typeof value !== "object" || value === null) return false
  const result = value as Partial<BulkUpdateSmithersDevicesResult>
  return typeof result.requestedCount === "number" && typeof result.successCount === "number" && typeof result.failureCount === "number" && Array.isArray(result.failedDevices)
}
